import PropTypes from 'prop-types';
import styles from './rewardBanks.module.scss';

/**
 * Read a reward-bank mutation without letting a failure disappear.
 *
 * Every handler on this screen used to do
 * `.then(({data: {createCashRewardBank}}) => …)`. When the request failed the
 * response carried no `data`, so the destructuring threw inside the promise —
 * no toast, no closed modal, no console message an admin would ever see. The
 * button just did nothing.
 *
 * Returns a plain result instead, so the caller has to look at it.
 */
export const readMutation = async (promise, field) => {
  try {
    const {data, errors} = await promise;
    if (errors?.length) {
      return {ok: false, message: errors[0].message};
    }
    const payload = data?.[field];
    if (!payload) {
      return {ok: false, message: 'No response from the server'};
    }
    // The result is a union — an Error looks exactly like a success until the
    // __typename is checked.
    if (payload.__typename === 'Error') {
      return {ok: false, message: payload.message || 'Something went wrong'};
    }
    return {ok: true, message: payload.message};
  } catch (error) {
    return {ok: false, message: error?.message || 'Something went wrong'};
  }
};

/** One figure in the stat row. */
export const statOf = (label, value, hint) => (
  <div key={label} className={styles.stat}>
    <span className={styles.statLabel}>{label}</span>
    <span className={styles.statValue}>
      {Number(value ?? 0).toLocaleString('en-US')}
    </span>
    {hint && <span className={styles.statHint}>{hint}</span>}
  </div>
);

/**
 * What is left in a bank, with how far through it is.
 *
 * A bare number says nothing about whether a bank is about to run dry, which
 * is the one thing worth seeing without opening anything. The bar turns amber
 * under a quarter left and red at empty.
 */
export const Remaining = ({used, total, left}) => {
  const usedNum = Number(used ?? 0);
  const totalNum = Number(total ?? 0);
  // `left` is trusted when the service sends it; the subtraction is only a
  // fallback for records that predate the field.
  const remaining = left ?? totalNum - usedNum;
  const pctUsed = totalNum > 0 ? Math.min(100, (usedNum / totalNum) * 100) : 0;
  const ratioLeft = totalNum > 0 ? remaining / totalNum : 1;

  const empty = remaining <= 0 && totalNum > 0;
  const low = !empty && ratioLeft <= 0.25;

  return (
    <div className={styles.remaining}>
      <span
        className={`${styles.remainingValue} ${
          empty ? styles.remainingEmpty : low ? styles.remainingLow : ''
        }`.trim()}>
        {Number(remaining).toLocaleString('en-US')}
      </span>
      <span className={styles.meter}>
        <span
          className={`${styles.meterFill} ${
            empty ? styles.meterFillEmpty : low ? styles.meterFillLow : ''
          }`.trim()}
          style={{width: `${pctUsed}%`}}
        />
      </span>
      <span className={styles.meterHint}>
        {totalNum > 0 ? `${Math.round(pctUsed)}% used` : 'Nothing deposited'}
      </span>
    </div>
  );
};

Remaining.propTypes = {
  used: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  left: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
